/**
 * The treemap's colour scale for a percent change: five steps, shared by the
 * tiles and the legend so a step can never be renamed in one and not the
 * other.
 *
 * Symmetric about zero and clamped at ±2%, in the legend's own words (V2
 * `4243:106729`: "−2% or worse · −1% · Flat · +1% · +2% or better"). Each
 * step is its own token (`--market-heat-*`, tokens.semantic.css), and each
 * step names the text drawn on it — tokens.test.ts holds every pair to
 * 4.5:1 in light, dark and the colourblind variant.
 */

export type TreemapBandId = 'strong-down' | 'down' | 'flat' | 'up' | 'strong-up'

export interface TreemapBand {
  label: string
  /** The fill and the text on it, as one pair. */
  className: string
  /** The figure under the name, where it sits a step quieter than the name. */
  valueClassName?: string
}

/**
 * Moves smaller than this either way read as Flat. Measured off both V2
 * frames rather than chosen: they draw −0.20%, +0.20% and +0.22% flat and
 * −0.28%, +0.28% and +0.31% coloured.
 */
export const FLAT_BAND = 0.25

/** Where the legend's "or worse" / "or better" begins — the clamp. */
export const STRONG_BAND = 2

export const TREEMAP_BANDS: Readonly<Record<TreemapBandId, TreemapBand>> = {
  'strong-down': {
    label: '−2% or worse',
    className: 'bg-market-heat-strong-down text-market-heat-strong-down-foreground',
  },
  down: { label: '−1%', className: 'bg-market-heat-down text-foreground' },
  flat: {
    label: 'Flat',
    className: 'bg-market-heat-flat text-foreground',
    valueClassName: 'text-muted-foreground',
  },
  up: { label: '+1%', className: 'bg-market-heat-up text-foreground' },
  'strong-up': {
    label: '+2% or better',
    className: 'bg-market-heat-strong-up text-market-heat-strong-up-foreground',
  },
}

/**
 * The Market Heatmap's own scale (`Treemap` `variant="heatmap"`, owner ruling 4 of 2026-10-28): the
 * mock paints its tiles in THREE flat fills, so the two middle steps take their direction's saturated
 * fill and the legend repeats it for −2% and −1%, +1% and +2%. The text on them is the strong step's
 * own ink, which already clears 4.5:1 on that fill.
 *
 * The colour-blind palette keeps its own five steps: `--heatmap-down` and `--heatmap-up` fall back to
 * the tinted middles there (the `mk:heatmap` block in tokens.semantic.css), so this table needs no
 * second set of classes.
 */
export const HEATMAP_BANDS: Readonly<Record<TreemapBandId, TreemapBand>> = {
  ...TREEMAP_BANDS,
  down: {
    label: '−1%',
    className: 'bg-heatmap-down text-heatmap-down-foreground',
  },
  up: {
    label: '+1%',
    className: 'bg-heatmap-up text-heatmap-up-foreground',
  },
}

/** Legend order, worst to best. */
export const TREEMAP_BAND_ORDER: readonly TreemapBandId[] = [
  'strong-down',
  'down',
  'flat',
  'up',
  'strong-up',
]

/**
 * Same rounding the printed figure gets (Intl, half-expand), so the colour is
 * always the colour of the number on the tile: a raw −0.2549 prints
 * "−0.25%" and must not be painted Flat for being a hair under the line.
 */
const TWO_PLACES = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
  useGrouping: false,
})

export const roundChange = (value: number): number => Number(TWO_PLACES.format(value))

export const bandFor = (percentChange: number): TreemapBandId => {
  const value = roundChange(percentChange)
  // NaN compares false everywhere and would otherwise fall through to the
  // strongest green — a missing figure is not a rally.
  if (Number.isNaN(value)) return 'flat'
  if (value <= -STRONG_BAND) return 'strong-down'
  if (value <= -FLAT_BAND) return 'down'
  if (value < FLAT_BAND) return 'flat'
  if (value < STRONG_BAND) return 'up'
  return 'strong-up'
}

const PERCENT = new Intl.NumberFormat('en-IN', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

/**
 * U+2212, matching lib/format — a hyphen is narrower than the plus. The sign
 * is read off the ROUNDED value, so −0.001 prints "0.00%", not "−0.00%".
 */
export const formatChange = (value: number): string => {
  const rounded = roundChange(value)
  return `${rounded > 0 ? '+' : rounded < 0 ? '−' : ''}${PERCENT.format(Math.abs(rounded))}%`
}

/**
 * The share map's fill scale (`Treemap`'s `share` variant): how much of its
 * group a tile accounts for, in four steps — a bigger share, a stronger fill.
 * One hue per group (the caller's up or down), so the steps are a sequential
 * scale inside that hue, never a second colour; every tile also prints its
 * share, so the fill is never the only way to read it.
 *
 * Under 10% · 10–25% · 25–50% · half or more. `TREEMAP_TONES` names the fill
 * each step paints.
 */
export type ShareStep = 1 | 2 | 3 | 4

export const shareStepFor = (share: number): ShareStep =>
  share >= 0.5 ? 4 : share >= 0.25 ? 3 : share >= 0.1 ? 2 : 1

/**
 * A share as the tile prints it: whole percent, never "0%" for a name that
 * holds some, nor "100%" for one that does not hold all.
 */
export const formatShare = (share: number): string => {
  const percent = Math.round(share * 100)
  if (percent === 0 && share > 0) return '<1%'
  if (percent === 100 && share < 1) return '>99%'
  return `${String(percent)}%`
}
